/**
 * Says so when a contact sync that was running on this screen finishes.
 *
 * The sync status is polled, so without this the only sign of a finished sync is a sentence that
 * quietly changes. The run being watched is remembered by its id, and the toast is about that run:
 * how many contacts it read, that it stopped early, or that it failed. A sync that had already
 * finished before the screen opened is not announced.
 */
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import type { Prospecting } from '@client/api/types';
import { fieldMapCopy } from '@client/features/crm-field-map/lib/field-map-copy';

interface WatchedSync {
  active?: Prospecting.SyncRun;
  lastCompleted?: Prospecting.SyncRun;
  lastFailure?: Prospecting.SyncRun;
}

export function useSyncFinishedToast(sync: WatchedSync, crmName: string) {
  const watchedRunId = useRef<string | null>(null);
  const { active, lastCompleted, lastFailure } = sync;

  useEffect(() => {
    const watched = watchedRunId.current;
    watchedRunId.current = active?.id ?? null;
    if (!watched || active) return;

    if (lastCompleted?.id === watched) {
      if (lastCompleted.clean) {
        toast.success(fieldMapCopy.sync.finished(crmName, lastCompleted.recordsSeen ?? 0));
      } else {
        toast.warning(fieldMapCopy.sync.finishedPartial(crmName));
      }
    } else if (lastFailure?.id === watched) {
      toast.error(fieldMapCopy.sync.finishedFailed(crmName));
    }
  }, [active, lastCompleted, lastFailure, crmName]);
}
